import { Box, Container, Flex, Heading, Text } from "@chakra-ui/react"
import { useState } from "react"

const faqs = [
  {
    q: "How much can retrofit reduce a property's energy bills?",
    a: "For most UK homes, the right combination of solar panels, insulation, and heating upgrades can eliminate 70–90% or more of grid energy costs. For properties with strong solar potential, smart export tariffs (such as Octopus Tenant Power, which passes below-market rates to tenants while landlords receive payments for surplus generation) can take a property to near-zero net energy costs and generate ongoing returns on the retrofit investment. GreenFlip models each home's specific upgrade path and projects the bill reduction and payback for each scenario."
  },
  {
    q: "How accurate are the assessments?",
    a: "GreenFlip models each property individually using satellite data, building geometry, and energy records from the Ordnance Survey and the national EPC register. Our heat loss and solar potential models are validated against metered generation and consumption data, and every projection shows the underlying data inputs so you can interrogate the assumptions behind each estimate."
  },
  {
    q: "What data does GreenFlip need to run an assessment?",
    a: "For most UK properties, a street address is enough to start. We source building characteristics, energy performance data, and local solar irradiance from public datasets automatically. For large portfolios, upload a spreadsheet of addresses and results return within minutes: no site visits, no scheduling, no manual data entry."
  },
  {
    q: "Does GreenFlip cover UK government funding schemes?",
    a: "Yes. The platform flags relevant funding pathways alongside each property's upgrade recommendations, including the Boiler Upgrade Scheme, the Social Housing Decarbonisation Fund, and local authority energy efficiency grants. Social landlords can filter their entire portfolio by funding eligibility."
  },
  {
    q: "How is GreenFlip different from an EPC?",
    a: "An EPC is a snapshot of current energy efficiency, assessed by a surveyor during a site visit, and valid for 10 years. GreenFlip produces a forward-looking investment case: specific upgrade costs, projected annual savings, ROI, and payback period for each retrofit path. It complements an EPC rather than replacing it, and runs in minutes rather than weeks."
  }
]

const ChevronIcon: React.FC<{ open: boolean }> = ({ open }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{
      transform: open ? "rotate(180deg)" : "rotate(0deg)",
      transition: "transform 0.2s ease",
      flexShrink: 0
    }}
  >
    <path d="M6 9l6 6 6-6" />
  </svg>
)

const FAQ: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null)

  return (
    <Box bg="white" borderTop="1px solid" borderColor="gray.100">
      <Container maxW="container.md" py={20}>
        <Heading
          as="h2"
          size="2xl"
          color="brand.700"
          mb={10}
          style={{ textWrap: "balance" } as React.CSSProperties}
        >
          Common questions
        </Heading>
        <Box>
          {faqs.map((faq, i) => (
            <Box key={i} borderBottom="1px solid" borderColor="gray.200">
              <Flex
                as="button"
                type="button"
                w="full"
                textAlign="left"
                py={5}
                align="center"
                justify="space-between"
                gap={4}
                onClick={() => setOpenIndex(openIndex === i ? null : i)}
                cursor="pointer"
                bg="transparent"
                border="none"
                color="brand.700"
                _hover={{ color: "brand.600" }}
                aria-expanded={openIndex === i}
              >
                <Text
                  fontWeight="semibold"
                  color="inherit"
                  textAlign="left"
                  fontSize="md"
                >
                  {faq.q}
                </Text>
                <ChevronIcon open={openIndex === i} />
              </Flex>
              <Box
                overflow="hidden"
                maxH={openIndex === i ? "600px" : "0"}
                transition="max-height 0.25s cubic-bezier(0,0,0.2,1)"
              >
                <Text color="gray.700" lineHeight="tall" pb={6} maxW="65ch">
                  {faq.a}
                </Text>
              </Box>
            </Box>
          ))}
        </Box>
      </Container>
    </Box>
  )
}

export default FAQ
